/**
 * @fileoverview Background music service implementing IAudioService.
 * Manages looping background music with autoplay workarounds for mobile.
 */

import { CONFIG } from '../core/GameConfig.js';
import { KonvaEventReal } from '../konva-extensions.js';
import { IAudioService } from './IAudioService.js';

/**
 * Manages background music with mobile compatibility.
 * Handles browser autoplay restrictions by starting muted and unmuting on user interaction.
 * Implements Page Visibility API for background/foreground handling.
 */
export class BackgroundMusicService implements IAudioService {
    private bgMusic: HTMLAudioElement | null = null;
    private _isPlaying: boolean = false;
    private _isMuted: boolean = false;
    private userMuted: boolean = false; // Track if user manually muted
    private onPlay: (() => void) | null = null;
    private _hasNotifiedPlay: boolean = false;
    private readonly MUTE_STORAGE_KEY = 'toddlerGameMuted';
    private wasPlayingBeforeHidden: boolean = false;
    
    /**
     * Load mute preference from localStorage.
     * @returns {boolean} Saved mute state, or false if not set
     */
    private loadMutePreference(): boolean {
        try {
            const saved = localStorage.getItem(this.MUTE_STORAGE_KEY);
            return saved === 'true';
        } catch (e) {
            console.log('Could not access localStorage:', e);
            return false;
        }
    }
    
    /**
     * Save mute preference to localStorage.
     * @param {boolean} muted - Mute state to save
     */
    private saveMutePreference(muted: boolean): void {
        try {
            localStorage.setItem(this.MUTE_STORAGE_KEY, muted.toString());
        } catch (e) {
            console.log('Could not save to localStorage:', e);
        }
    }
    
    /**
     * Handle play notification callback - calls onPlay once.
     */
    private notifyPlayStarted(): void {
        if (!this._hasNotifiedPlay) {
            this._hasNotifiedPlay = true;
            if (typeof this.onPlay === 'function') {
                this.onPlay();
            }
        }
    }
    
    /**
     * Attempt to play audio with promise handling.
     * @param {string} context - Context for error logging
     * @returns {boolean} True if play was attempted
     */
    private attemptPlay(context: string): boolean {
        if (!this.bgMusic) return false;
        
        const playPromise = this.bgMusic.play();
        
        if (playPromise !== undefined) {
            playPromise.then(() => {
                this._isPlaying = true;
                this.notifyPlayStarted();
            }).catch((err: any) => {
                console.log(context + ' failed:', err);
            });
        } else {
            this._isPlaying = true;
            this.notifyPlayStarted();
        }
        
        return true;
    }
    
    /**
     * Initialize background music - prepares audio element without playing.
     * Audio will start on explicit user interaction (e.g., tap to start button).
     * Loads saved mute preference from localStorage.
     */
    init(): void {
        if (!CONFIG.AUDIO.ENABLED) return;
        
        // Load saved mute preference
        const savedMuted = this.loadMutePreference();
        this.userMuted = savedMuted;
        this._isMuted = savedMuted;
        
        this.bgMusic = new Audio(CONFIG.AUDIO.BACKGROUND_MUSIC_PATH);
        this.bgMusic.loop = CONFIG.AUDIO.LOOP;
        this.bgMusic.volume = CONFIG.AUDIO.VOLUME;
        this.bgMusic.muted = savedMuted; // Apply saved preference
        
        // Setup visibility handling for background/foreground
        this.setupVisibilityHandling();
        
        // Don't attempt autoplay - wait for explicit user interaction
    }
    
    /**
     * Setup Page Visibility API to pause/resume audio when app goes to background/foreground.
     */
    private setupVisibilityHandling(): void {
        document.addEventListener('visibilitychange', () => {
            if (document.hidden) {
                // App going to background - pause if playing
                if (this._isPlaying && this.bgMusic && !this.bgMusic.paused) {
                    this.wasPlayingBeforeHidden = true;
                    this.bgMusic.pause();
                }
            } else {
                // App coming to foreground - resume if it was playing
                if (this.wasPlayingBeforeHidden && this.bgMusic) {
                    this.attemptPlay('Resume from background');
                    this.wasPlayingBeforeHidden = false;
                }
            }
        });
    }
    
    /**
     * Play background music.
     * Handles promise-based and callback-based play() APIs.
     */
    play(): void {
        if (!this.bgMusic) return;
        
        const playPromise = this.bgMusic.play();
        
        if (playPromise !== undefined) {
            playPromise.then(() => {
                this._isPlaying = true;
            }).catch((error: any) => {
                console.log('Play prevented or failed:', error);
            });
        } else {
            this._isPlaying = true;
        }
    }
    
    /**
     * Start audio from a user interaction event (for iOS compatibility).
     * @returns {boolean} True if audio started successfully
     */
    startFromUserInteraction(): boolean {
        if (!this.bgMusic) return false;
        
        try {
            this.bgMusic.muted = false;
            this._isMuted = false;
            return this.attemptPlay('Audio start');
        } catch (e) {
            console.log('startFromUserInteraction error:', e);
            return false;
        }
    }
    
    /**
     * Pause background music and update playing state.
     */
    pause(): void {
        if (this.bgMusic) {
            this.bgMusic.pause();
            this._isPlaying = false;
        }
    }

    /**
     * Lower volume (for dramatic moments like celebrations).
     */
    lowerVolume(): void {
        if (this.bgMusic && this._isPlaying) {
            this.bgMusic.volume = CONFIG.AUDIO.LOWER_VOLUME;
        }
    }

    /**
     * Restore normal volume.
     */
    normalVolume(): void {
        if (this.bgMusic && this._isPlaying) {
            this.bgMusic.volume = CONFIG.AUDIO.VOLUME;
        }
    }
    
    /**
     * Resume background music if it was playing before.
     */
    resume(): void {
        if (this.bgMusic && !this._isMuted) {
            this.attemptPlay('Resume music');
        }
    }
    
    /**
     * Toggle mute state and save preference to localStorage.
     * If unmuting and audio hasn't started, begin playback.
     * @returns {boolean} New mute state (true if muted, false if unmuted)
     */
    toggleMute(): boolean {
        this._isMuted = !this._isMuted;
        this.userMuted = this._isMuted;
        
        if (this.bgMusic) {
            this.bgMusic.muted = this._isMuted;
            
            // If unmuting and audio hasn't started playing yet, start it
            if (!this._isMuted && !this._isPlaying) {
                this.attemptPlay('Play on unmute');
            }
        }
        
        this.saveMutePreference(this._isMuted);
        return this._isMuted;
    }
    
    /**
     * Set volume level.
     * @param {number} volume - Volume level between 0.0 (silent) and 1.0 (max)
     */
    setVolume(volume: number): void {
        if (this.bgMusic) {
            this.bgMusic.volume = Math.max(0, Math.min(1, volume));
        }
    }

    /**
     * Check if audio is currently playing.
     * @returns {boolean} True if playing, false otherwise
     */
    isPlaying(): boolean {
        return this._isPlaying;
    }

    /**
     * Check if audio is muted.
     * @returns {boolean} True if muted, false otherwise
     */
    isMuted(): boolean {
        return this._isMuted;
    }

    /**
     * Try to unlock audio from a trusted user event.
     * Must be called directly from a user interaction (dragstart, click, touch).
     * Only unmutes if the user hasn't manually muted the audio.
     * @param {any} evt - The originating user event
     * @returns {boolean} True if unlock was attempted, false if event was invalid
     */
    tryUnlockFromEvent(evt: KonvaEventReal): boolean {
        try {
            if (!evt || evt.isTrusted === false) {
                return false;
            }
        } catch (e) {
            // ignore
        }

        if (!this.bgMusic) return false;

        try {
            // Only unmute if user hasn't manually muted
            if (!this.userMuted) {
                this.bgMusic.muted = false;
                this._isMuted = false;
            }
            
            // Always try to play if not already playing
            if (!this._isPlaying) {
                this.attemptPlay('tryUnlockFromEvent play');
            }
            
            return true;
        } catch (e) {
            console.log('tryUnlockFromEvent error', e);
            return false;
        }
    }

    /**
     * Set a callback to be invoked when playback starts.
     * @param {() => void} callback - Function to call on play start
     */
    setOnPlayCallback(callback: (() => void) | null): void {
        this.onPlay = callback;
    }
}
